import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
// react-easy-crop ships its container/positioning CSS as a separate file - without it,
// the crop container never gets sized/positioned, so the image just renders at its
// natural pixel size floating in the modal instead of filling and cropping the frame.
import 'react-easy-crop/react-easy-crop.css';

// Matches the live hero banner box's widest (desktop) shape - see Home.js HeroCarousel.
// Cropping against this ratio and letting object-fit: cover handle the narrower mobile
// box on top of it keeps the crop centered/predictable across screen sizes instead of
// guessing where an uncropped image's important content sits.
export const BANNER_ASPECT_RATIO = 1238 / 140; // ~8.84 : 1

// Matches the Special Offer strip's box on desktop (1240x144) - close enough to the top
// banner's shape (its height is content-driven, not fixed, so this is an approximation)
// that reusing the same ratio is simpler than maintaining a near-identical constant.
export const SPECIAL_OFFER_ASPECT_RATIO = BANNER_ASPECT_RATIO;

// Left/Right promo tiles sit side by side (2/3 + 1/3 width) when both are enabled - the
// most common setup. If an admin enables only one, it stretches to the full row and ends
// up wider than this ratio; a mismatch there is minor compared to the two-tile default.
export const LEFT_ASPECT_RATIO = 820 / 260; // ~3.15 : 1
export const RIGHT_ASPECT_RATIO = 401 / 260; // ~1.54 : 1

// Mobile counterparts - measured at a 375px-wide viewport. Each slot's mobile box is a
// meaningfully different shape than its desktop box (narrower relative to height), which
// is why a single crop can't serve both without cutting off side content - see the
// two-stage crop queue in AdminSettings.js.
export const MOBILE_BANNER_ASPECT_RATIO = 350 / 100; // 3.5 : 1
export const MOBILE_SPECIAL_OFFER_ASPECT_RATIO = 351 / 116; // ~3.03 : 1
export const MOBILE_LEFT_ASPECT_RATIO = 350 / 140; // 2.5 : 1
export const MOBILE_RIGHT_ASPECT_RATIO = 350 / 140; // 2.5 : 1 (Left/Right stack full-width on mobile)

// Header site logo - rendered with object-contain (never cropped by CSS, always
// letterboxed to fit), so this frame exists to help the admin trim excess whitespace
// around their raw logo file rather than to prevent CSS from cutting anything off.
// Square (60x60) fits a circular/oval mark like Viverra's far better than a wide
// rectangle - the ring no longer gets squeezed into a thin horizontal strip.
export const LOGO_ASPECT_RATIO = 1; // 60 : 60 (square)

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Small source files (e.g. a 90x90 logo) can end up rendering tiny/cramped inside the
// crop frame regardless of objectFit mode, leaving little room to drag or judge the
// crop. If the larger dimension is under this floor, upscale it before handing it to
// the cropper so there's always a reasonably sized image to work with.
const MIN_SOURCE_DIMENSION = 500;

async function ensureMinimumSize(dataUrl) {
  const img = await loadImage(dataUrl);
  const largerDimension = Math.max(img.naturalWidth, img.naturalHeight);
  if (largerDimension >= MIN_SOURCE_DIMENSION || largerDimension === 0) return dataUrl;

  const scale = Math.min(MIN_SOURCE_DIMENSION / largerDimension, 6); // cap to avoid absurd upscales
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

async function getCroppedBlob(imageSrc, cropPixels, mimeType) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType || 'image/jpeg', 0.92);
  });
}

/**
 * Modal that lets an admin position/zoom an uploaded image inside a frame shaped
 * exactly like the live banner box, then hands back a single cropped Blob.
 *
 * Props:
 *  - file: the raw File selected by the admin (required to open)
 *  - onCancel(): skip this file
 *  - onConfirm(blob, fileName): called with the cropped image Blob
 *  - queueInfo: optional "2 of 5" style label when cropping multiple uploads in sequence
 *  - aspectRatio: target width/height ratio for the crop frame (defaults to the top
 *    banner's shape). Pass the matching constant for whichever slot is being cropped -
 *    left/right promo tiles and the special offer strip are shaped differently.
 *  - stageLabel: optional "Mobile View" / "Desktop View" badge - each upload goes
 *    through the modal twice (once per screen shape), this says which pass is active.
 */
export default function BannerCropModal({ file, onCancel, onConfirm, queueInfo, aspectRatio, stageLabel }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (file) {
      readFileAsDataUrl(file)
        .then((dataUrl) => ensureMinimumSize(dataUrl))
        .then((dataUrl) => {
          if (!cancelled) setImageSrc(dataUrl);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [file]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixelsValue) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, file.type);
      onConfirm(blob, file.name);
    } finally {
      setSaving(false);
    }
  };

  if (!file) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              Position Banner Image
              {stageLabel && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {stageLabel}
                </span>
              )}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Drag to reposition, scroll or pinch to zoom. The frame shows exactly what visitors will see.
              {queueInfo ? ` (${queueInfo})` : ''}
            </p>
          </div>
        </div>

        <div className="relative w-full bg-gray-900" style={{ height: 320 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio || BANNER_ASPECT_RATIO}
              // "contain" (the library default) shows the WHOLE image at zoom=1,
              // letterboxed if its aspect ratio doesn't match the frame - the admin only
              // loses content they deliberately zoom into. "cover" was tried instead but
              // forces the frame to be completely filled even at minimum zoom, which
              // means a mismatched source (e.g. a wide logo into a square frame) gets
              // its sides cropped away with no way to back off and see the whole thing.
              objectFit="contain"
              restrictPosition={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-500 w-12">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              Skip this image
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving || !croppedAreaPixels}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Use This Crop'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
